import React, { useEffect, useState } from "react";
import { showValidationToast, showApiErrorToast } from "../../../utils/toastUtils";
import FormModal from "../../../components/form/FormModal";
import FormCheckbox from "../../../components/form/FormCheckbox";
import FormField from "../../../components/form/FormField";
import FormInput from "../../../components/form/FormInput";
import FormSelect from "../../../components/form/FormSelect";
import FormTextarea from "../../../components/form/FormTextarea";
import SearchableMultiSelect from "../../../components/form/SearchableMultiSelect";
import "./promo.css";
import { listVenues } from "../Venues/venuesApi";
import { listUsers } from "../Users/userApi";
import { listGames } from "../Games/gameApi";

const toLocalDateTimeInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  const pad = (n) => String(n).padStart(2, "0");

  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const emptyForm = {
  code: "",
  description: "",
  discountType: "flat",
  discountValue: "",
  maxDiscount: "",
  minBookingAmount: "",
  usageLimit: "",
  perUserLimit: "1",
  validFrom: "",
  validUntil: "",
  isActive: true,
  applicableVenues: [],
  applicableSports: [],
  userType: "all",
  specificUsers: [],
  fundedBy: "platform",
};

const PromoForm = ({
  open,
  mode = "create",
  initialData = null,
  externalErrors = {},
  onExternalErrorChange,
  onCancel,
  onSubmit,
  submitting = false,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [venueOptions, setVenueOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [gameOptions, setGameOptions] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);

  useEffect(() => {
    setErrors({});

    if (!initialData) {
      setForm(emptyForm);
      return;
    }

    setForm({
      code: initialData.code || "",
      description: initialData.description || "",
      discountType: initialData.discountType || "flat",
      discountValue:
        initialData.discountValue !== undefined && initialData.discountValue !== null
          ? String(initialData.discountValue)
          : "",
      maxDiscount:
        initialData.maxDiscount !== undefined && initialData.maxDiscount !== null
          ? String(initialData.maxDiscount)
          : "",
      minBookingAmount:
        initialData.minBookingAmount !== undefined &&
        initialData.minBookingAmount !== null
          ? String(initialData.minBookingAmount)
          : "",
      usageLimit:
        initialData.usageLimit !== undefined && initialData.usageLimit !== null
          ? String(initialData.usageLimit)
          : "",
      perUserLimit:
        initialData.perUserLimit !== undefined && initialData.perUserLimit !== null
          ? String(initialData.perUserLimit)
          : "1",
      validFrom: toLocalDateTimeInput(initialData.validFrom),
      validUntil: toLocalDateTimeInput(initialData.validUntil),
      isActive:
        typeof initialData.isActive === "boolean" ? initialData.isActive : true,
      applicableVenues: Array.isArray(initialData.applicableVenues)
        ? initialData.applicableVenues.map((v) =>
            String(typeof v === "string" ? v : v._id),
          )
        : [],
      applicableSports: Array.isArray(initialData.applicableSports)
        ? initialData.applicableSports.map((g) =>
            String(typeof g === "string" ? g : g._id),
          )
        : [],
      userType: initialData.userType || "all",
      specificUsers: Array.isArray(initialData.specificUsers)
        ? initialData.specificUsers.map((u) =>
            String(typeof u === "string" ? u : u._id),
          )
        : [],
      fundedBy: initialData.fundedBy || "platform",
    });
  }, [initialData]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        setLoadingMeta(true);

        const [venueRes, userRes, gameRes] = await Promise.all([
          listVenues({ page: 1, pageSize: 200, q: "" }),
          listUsers({ page: 1, limit: 200, search: "" }),
          listGames({ page: 1, limit: 200, search: "" }),
        ]);

        const venues = Array.isArray(venueRes?.items) ? venueRes.items : [];
        const rawUsers = Array.isArray(userRes?.data?.users)
          ? userRes.data.users
          : [];
        const games = Array.isArray(gameRes) ? gameRes : [];

        setVenueOptions(
          venues.map((v) => ({
            value: String(v._id || v.id),
            label: v.name || v.title || "Unnamed Venue",
          })),
        );

        setUserOptions(
          rawUsers.map((u) => ({
            value: String(u._id || u.id),
            label:
              u.fullName || u.name
                ? `${u.fullName || u.name}${
                    u.email
                      ? ` • ${u.email}`
                      : u.mobileNumber
                      ? ` • ${u.mobileNumber}`
                      : ""
                  }`
                : String(u.email || u.mobileNumber || "Unnamed User"),
          })),
        );

        setGameOptions(
          games.map((g) => ({
            value: String(g._id || g.id),
            label: g.name || g.title || "Unnamed Game",
          })),
        );
      } catch (e) {
        console.error("Promo form meta load failed", e);
        showApiErrorToast(e, "Unable to load form data");
      } finally {
        setLoadingMeta(false);
      }
    };

    loadMeta();
  }, []);

  const set = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const clearCodeError = () => {
    setErrors((prev) => {
      if (!prev.code) return prev;
      const next = { ...prev };
      delete next.code;
      return next;
    });

    if (externalErrors.code) {
      onExternalErrorChange?.((prev) => {
        const next = { ...prev };
        delete next.code;
        return next;
      });
    }
  };

  const validate = () => {
    const e = {};

    if (!form.code.trim()) e.code = "Promo code is required";

    const discountValue = Number(form.discountValue);
    if (
      form.discountValue === "" ||
      Number.isNaN(discountValue) ||
      discountValue <= 0
    ) {
      e.discountValue = "Discount value must be greater than 0";
    }

    if (form.discountType === "percentage" && discountValue > 100) {
      e.discountValue = "Percentage cannot be more than 100";
    }

    if (form.maxDiscount && Number(form.maxDiscount) < 0) {
      e.maxDiscount = "Max discount cannot be negative";
    }

    if (form.minBookingAmount && Number(form.minBookingAmount) < 0) {
      e.minBookingAmount = "Minimum booking amount cannot be negative";
    }

    if (form.usageLimit && Number(form.usageLimit) < 1) {
      e.usageLimit = "Usage limit must be at least 1";
    }

    if (!form.perUserLimit || Number(form.perUserLimit) < 1) {
      e.perUserLimit = "Per user limit must be at least 1";
    }

    if (
      form.validFrom &&
      form.validUntil &&
      new Date(form.validFrom) > new Date(form.validUntil)
    ) {
      e.validUntil = "Valid until must be after valid from";
    }

    if (
      form.userType === "specific" &&
      (!Array.isArray(form.specificUsers) || !form.specificUsers.length)
    ) {
      e.specificUsers = "Select at least one user";
    }

    setErrors(e);
    return e;
  };

  const submit = (e) => {
    e.preventDefault();

    if (submitting) return;

    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      showValidationToast(validationErrors);
      return;
    }

    const payload = {
      code: form.code.trim().toUpperCase(),
      description: form.description.trim(),
      discountType: form.discountType,
      discountValue: Number(form.discountValue),
      maxDiscount:
        form.discountType === "percentage" && form.maxDiscount !== ""
          ? Number(form.maxDiscount)
          : null,
      minBookingAmount:
        form.minBookingAmount !== "" ? Number(form.minBookingAmount) : 0,
      usageLimit: form.usageLimit !== "" ? Number(form.usageLimit) : null,
      perUserLimit: form.perUserLimit !== "" ? Number(form.perUserLimit) : 1,
      validFrom: form.validFrom ? new Date(form.validFrom).toISOString() : null,
      validUntil: form.validUntil ? new Date(form.validUntil).toISOString() : null,
      isActive: !!form.isActive,
      applicableVenues: Array.isArray(form.applicableVenues)
        ? form.applicableVenues
        : [],
      applicableSports: Array.isArray(form.applicableSports)
        ? form.applicableSports
        : [],
      userType: form.userType,
      specificUsers:
        form.userType === "specific" && Array.isArray(form.specificUsers)
          ? form.specificUsers
          : [],
      fundedBy: form.fundedBy,
    };

    onSubmit?.(payload);
  };

  return (
    <FormModal
      open={open}
      title={mode === "create" ? "Create Promo Code" : "Edit Promo Code"}
      width="800px"
      submitting={submitting}
      submitText={mode === "create" ? "Create Promo" : "Save Changes"}
      submittingText="Saving..."
      cancelText="Cancel"
      onClose={onCancel}
      onSubmit={submit}
    >
      <div className="promo-form">
        <div className="promo-grid">
          <FormField label="Promo Code" error={errors.code || externalErrors.code}>
            <FormInput
              type="text"
              value={form.code}
              onChange={(e) => {
                set("code", e.target.value.toUpperCase());
                clearCodeError();
              }}
              placeholder="PLAY50"
              maxLength={40}
            />
          </FormField>

          <FormField label="Discount Type">
            <FormSelect
              value={form.discountType}
              onChange={(e) => set("discountType", e.target.value)}
            >
              <option value="flat">Flat</option>
              <option value="percentage">Percentage</option>
            </FormSelect>
          </FormField>

          <FormField label="Description" className="promo-span-2">
            <FormTextarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Flat 50 off on booking"
              rows={3}
            />
          </FormField>

          <FormField label="Discount Value" error={errors.discountValue}>
            <FormInput
              type="number"
              min="1"
              value={form.discountValue}
              onChange={(e) => set("discountValue", e.target.value)}
              placeholder={form.discountType === "percentage" ? "20" : "50"}
            />
          </FormField>

          {form.discountType === "percentage" && (
            <FormField label="Max Discount" error={errors.maxDiscount}>
              <FormInput
                type="number"
                min="0"
                value={form.maxDiscount}
                onChange={(e) => set("maxDiscount", e.target.value)}
                placeholder="100"
              />
            </FormField>
          )}

          <FormField
            label="Minimum Booking Amount"
            error={errors.minBookingAmount}
          >
            <FormInput
              type="number"
              min="0"
              value={form.minBookingAmount}
              onChange={(e) => set("minBookingAmount", e.target.value)}
              placeholder="200"
            />
          </FormField>

          <FormField label="Usage Limit" error={errors.usageLimit}>
            <FormInput
              type="number"
              min="1"
              value={form.usageLimit}
              onChange={(e) => set("usageLimit", e.target.value)}
              placeholder="1000"
            />
          </FormField>

          <FormField label="Per User Limit" error={errors.perUserLimit}>
            <FormInput
              type="number"
              min="1"
              value={form.perUserLimit}
              onChange={(e) => set("perUserLimit", e.target.value)}
              placeholder="1"
            />
          </FormField>

          <FormField label="Valid From">
            <FormInput
              type="datetime-local"
              value={form.validFrom}
              onChange={(e) => set("validFrom", e.target.value)}
            />
          </FormField>

          <FormField label="Valid Until" error={errors.validUntil}>
            <FormInput
              type="datetime-local"
              value={form.validUntil}
              onChange={(e) => set("validUntil", e.target.value)}
            />
          </FormField>

          <FormField label="User Type">
            <FormSelect
              value={form.userType}
              onChange={(e) => set("userType", e.target.value)}
            >
              <option value="all">All Users</option>
              <option value="new">New Users</option>
              <option value="specific">Specific Users</option>
            </FormSelect>
          </FormField>

          {form.userType === "specific" ? (
            <FormField
              label="Specific Users"
              error={errors.specificUsers}
              hint="Only selected users can use this promo."
            >
              <SearchableMultiSelect
                options={userOptions}
                value={form.specificUsers}
                onChange={(next) => set("specificUsers", next)}
                placeholder={
                  loadingMeta
                    ? "Loading users..."
                    : "Search users by name, email or phone"
                }
                emptyText="No users found"
                disabled={loadingMeta}
              />
            </FormField>
          ) : (
            <FormField label="Funded By">
              <FormSelect
                value={form.fundedBy}
                onChange={(e) => set("fundedBy", e.target.value)}
              >
                <option value="platform">Platform</option>
                <option value="venue">Venue</option>
              </FormSelect>
            </FormField>
          )}

          {form.userType === "specific" && (
            <FormField label="Funded By">
              <FormSelect
                value={form.fundedBy}
                onChange={(e) => set("fundedBy", e.target.value)}
              >
                <option value="platform">Platform</option>
                <option value="venue">Venue</option>
              </FormSelect>
            </FormField>
          )}

          <FormField
            label="Applicable Venues"
            hint="Leave empty if promo should work for all venues."
            className="promo-span-2"
          >
            <SearchableMultiSelect
              options={venueOptions}
              value={form.applicableVenues}
              onChange={(next) => set("applicableVenues", next)}
              placeholder={
                loadingMeta ? "Loading venues..." : "Search and select venues"
              }
              emptyText="No venues found"
              disabled={loadingMeta}
            />
          </FormField>

          <FormField
            label="Applicable Games"
            hint="Leave empty if promo should work for all games."
            className="promo-span-2"
          >
            <SearchableMultiSelect
              options={gameOptions}
              value={form.applicableSports}
              onChange={(next) => set("applicableSports", next)}
              placeholder={
                loadingMeta ? "Loading games..." : "Search and select games"
              }
              emptyText="No games found"
              disabled={loadingMeta}
            />
          </FormField>

          <div className="promo-span-2">
            <FormCheckbox
              checked={!!form.isActive}
              onChange={(e) => set("isActive", e.target.checked)}
              label="Active (visible and usable)"
            />
          </div>
        </div>
      </div>
    </FormModal>
  );
};

export default PromoForm;